import { motion } from "framer-motion";
import { MapPin, TrendingUp, Building2, Users, ArrowUpRight } from "lucide-react";

const markets = [
  {
    id: 1,
    city: "Dubai",
    country: "UAE",
    flag: "🇦🇪",
    description: "Architectural marvels and tax-free investments",
    stats: {
      avgPrice: "€3.2M",
      growth: "+18%",
      properties: "45+",
    },
    position: { top: "35%", left: "72%" },
    highlights: ["Palm Jumeirah", "Downtown Dubai", "Emirates Hills"],
  },
  {
    id: 2,
    city: "Monaco",
    country: "Monte Carlo",
    flag: "🇲🇨",
    description: "The pinnacle of Mediterranean luxury",
    stats: {
      avgPrice: "€8.5M",
      growth: "+12%",
      properties: "20+",
    },
    position: { top: "32%", left: "48%" },
    highlights: ["Port Hercules", "Monte Carlo", "Larvotto"],
  },
  {
    id: 3,
    city: "Marbella",
    country: "Spain",
    flag: "🇪🇸",
    description: "Golden Mile exclusivity meets Andalusian charm",
    stats: {
      avgPrice: "€2.8M",
      growth: "+15%",
      properties: "60+",
    },
    position: { top: "42%", left: "38%" },
    highlights: ["Golden Mile", "Puerto Banús", "La Zagaleta"],
  },
  {
    id: 4,
    city: "Tuscany",
    country: "Italy",
    flag: "🇮🇹",
    description: "Historic estates with timeless appreciation",
    stats: {
      avgPrice: "€4.1M",
      growth: "+10%",
      properties: "35+",
    },
    position: { top: "30%", left: "50%" },
    highlights: ["Florence", "Chianti", "Amalfi Coast"],
  },
];

const GlobalNetworkSection = () => {
  return (
    <section id="network" className="py-24 lg:py-32 bg-secondary/30 overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-4">
            Global Presence
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground mb-6 leading-tight">
            Connecting Markets
            <span className="block italic font-medium">Across Continents</span>
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
            With nearly three decades of experience bridging international markets, we connect discerning investors 
            with extraordinary opportunities across Europe and the Middle East.
          </p>
        </motion.div>

        {/* Interactive Map Visualization */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="relative mb-16"
        >
          {/* Stylized World Map Background */}
          <div className="relative aspect-[2/1] bg-gradient-to-br from-card via-card/80 to-secondary rounded-3xl border border-border/50 overflow-hidden">
            {/* Grid Pattern */}
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-primary" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>
            </div>

            {/* Connection Lines */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 50" preserveAspectRatio="none">
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                  <stop offset="50%" stopColor="hsl(var(--primary))" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* Curved connection lines between markets */}
              <path
                d="M 38 21 Q 44 15 50 16"
                fill="none"
                stroke="url(#lineGradient)"
                strokeWidth="0.15"
                className="animate-pulse"
              />
              <path
                d="M 50 16 Q 55 12 48 16"
                fill="none"
                stroke="url(#lineGradient)"
                strokeWidth="0.15"
                className="animate-pulse"
              />
              <path
                d="M 48 16 Q 60 20 72 17"
                fill="none"
                stroke="url(#lineGradient)"
                strokeWidth="0.15"
                className="animate-pulse"
              />
              <path
                d="M 38 21 Q 55 30 72 17"
                fill="none"
                stroke="url(#lineGradient)"
                strokeWidth="0.15"
                className="animate-pulse"
              />
            </svg>

            {/* Market Location Pins */}
            {markets.map((market, index) => (
              <motion.div
                key={market.id}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.15 }}
                viewport={{ once: true }}
                className="absolute group cursor-pointer"
                style={{ top: market.position.top, left: market.position.left }}
              >
                {/* Pulse Ring */}
                <div className="absolute -inset-4 bg-primary/20 rounded-full animate-ping" />
                <div className="absolute -inset-2 bg-primary/30 rounded-full" />
                
                {/* Pin */}
                <div className="relative w-4 h-4 bg-primary rounded-full border-2 border-background shadow-lg z-10" />
                
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-20">
                  <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-luxury whitespace-nowrap">
                    <p className="font-display text-sm text-foreground flex items-center gap-2">
                      <span>{market.flag}</span>
                      {market.city}
                    </p>
                    <p className="text-xs text-primary font-medium">{market.stats.avgPrice} avg</p>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Central Hub Indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.8 }}
              viewport={{ once: true }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            >
              <div className="relative">
                <div className="absolute -inset-8 bg-primary/5 rounded-full animate-pulse" />
                <div className="absolute -inset-4 bg-primary/10 rounded-full" />
                <div className="w-3 h-3 bg-primary/50 rounded-full" />
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Market Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {markets.map((market, index) => (
            <motion.div
              key={market.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="bg-card border border-border/50 rounded-2xl p-6 h-full transition-all duration-500 hover:border-primary/30 hover:shadow-luxury">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">{market.flag}</span>
                      <h3 className="font-display text-xl text-foreground">{market.city}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">{market.country}</p>
                  </div>
                  <div className="flex items-center gap-1 text-green-500 text-sm font-medium">
                    <TrendingUp className="w-4 h-4" />
                    {market.stats.growth}
                  </div>
                </div>

                {/* Description */}
                <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                  {market.description}
                </p>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                      <Building2 className="w-3.5 h-3.5" />
                      <span className="text-xs uppercase tracking-wider">Avg. Price</span>
                    </div>
                    <p className="font-display text-lg text-primary font-semibold">
                      {market.stats.avgPrice}
                    </p>
                  </div>
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                      <Users className="w-3.5 h-3.5" />
                      <span className="text-xs uppercase tracking-wider">Listings</span>
                    </div>
                    <p className="font-display text-lg text-foreground font-semibold">
                      {market.stats.properties}
                    </p>
                  </div>
                </div>

                {/* Highlights */}
                <div className="flex flex-wrap gap-2">
                  {market.highlights.map((highlight) => (
                    <span
                      key={highlight}
                      className="text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full"
                    >
                      {highlight}
                    </span>
                  ))}
                </div>

                {/* Hover Arrow */}
                <div className="mt-4 pt-4 border-t border-border/30 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-sm text-muted-foreground">Explore market</span>
                  <ArrowUpRight className="w-4 h-4 text-primary" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-8 md:gap-12 bg-card/50 backdrop-blur-sm border border-border/30 rounded-full px-8 py-4">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-primary" />
              <div className="text-left">
                <span className="font-display text-xl text-foreground font-semibold">4</span>
                <p className="text-xs text-muted-foreground">Countries</p>
              </div>
            </div>
            <div className="w-px h-8 bg-border/50" />
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-primary" />
              <div className="text-left">
                <span className="font-display text-xl text-foreground font-semibold">160+</span>
                <p className="text-xs text-muted-foreground">Properties</p>
              </div>
            </div>
            <div className="w-px h-8 bg-border/50" />
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-primary" />
              <div className="text-left">
                <span className="font-display text-xl text-foreground font-semibold">14%</span>
                <p className="text-xs text-muted-foreground">Avg. Growth</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GlobalNetworkSection;
